// SPDX-License-Identifier: MIT
/**
 * Named curves with a feel, so scenes talk about intent ("snap", "glide") instead of maths.
 * Rule of thumb: entrances decelerate (glide), exits accelerate (launch), and moves between two
 * rest states use an in-out curve (silk for calm, snap for punchy).
 */
import { cubicBezier } from '../core/bezier.ts';
import type { Ease } from '../core/ease.ts';
import { clamp } from '../core/math.ts';
import { spring as springAt } from '../core/spring.ts';
/** The closed-form spring as a 0→1 curve: it settles within the segment and ends exactly on 1. */
function springCurve(settle = 0.7): Ease {
    const end = springAt(settle);
    return (k) => {
        const x = clamp(k);
        return springAt(x * settle) + (1 - end) * x ** 3;
    };
}
export const curves = {
    /** Calm, symmetrical in-out. Camera moves, slow pushes. */
    silk: cubicBezier(0.65, 0, 0.35, 1),
    /** Very sharp in-out: long anticipation, fast middle, long settle. Hard cuts of motion. */
    snap: cubicBezier(0.85, 0, 0.15, 1),
    /** Fast start, long soft landing. Entrances, reveals. */
    glide: cubicBezier(0.16, 1, 0.3, 1),
    /** Slow start, fast exit. Things leaving frame. */
    launch: cubicBezier(0.7, 0, 0.84, 0),
    /** Lands past the target and settles back. Playful pops; use sparingly. */
    overshoot: cubicBezier(0.34, 1.56, 0.64, 1),
    /** Pulls back before going. Anticipation before a hit. */
    anticipate: cubicBezier(0.6, -0.28, 0.735, 0.045),
    /** Material-style standard curve. Neutral UI-like motion. */
    standard: cubicBezier(0.4, 0, 0.2, 1),
    /** Damped spring, about 5% past the target and back. Toggles, cards snapping into place. */
    spring: springCurve(),
} as const;
export type CurveName = keyof typeof curves;
